import { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react'
import TodayRoundedIcon from '@mui/icons-material/TodayRounded'
import AssignmentRoundedIcon from '@mui/icons-material/AssignmentRounded'
import MoreHorizRoundedIcon from '@mui/icons-material/MoreHorizRounded'
import NotificationsNoneRoundedIcon from '@mui/icons-material/NotificationsNoneRounded'
import TrendingUpRoundedIcon from '@mui/icons-material/TrendingUpRounded'
import EventAvailableRoundedIcon from '@mui/icons-material/EventAvailableRounded'
import SchoolRoundedIcon from '@mui/icons-material/SchoolRounded'
import type { FamilyLearnerRow } from '../../services/eduFamilyService'
import {
  ChildSwitcher,
  type ChildSwitcherItem,
  FamilyTabs,
  type FamilyTabSpec,
  Button,
  IconButton,
  AppDialog,
  type AppDialogAction,
  InboxItemRow,
  FamilyCard,
  Toggle,
  SegmentedControl,
  MiniLoader,
} from '../Common'
import { FamilyTodayTab } from './FamilyTodayTab'
import { FamilyBookTab } from './FamilyBookTab'
import { FamilyWorkTab } from './FamilyWorkTab'
import { FamilyProgressTab } from './FamilyProgressTab'
import { FamilyClassTab } from './FamilyClassTab'
import { FamilyMoreTab } from './FamilyMoreTab'
import { ScreenGuideHost } from '../ScreenGuide/ScreenGuideHost'
import { TourGuideContext } from '../tourguide/tourGuideContextCore'
import { familyScreenGuideId } from './familyScreenGuides'
import { listFamilyAnnouncements } from '../../services/eduAnnouncementService'
import { listFamilyFeed } from '../../services/eduFeedService'
import { listEduThreads } from '../../services/eduMessagingService'
import {
  getEduMediaConsent,
  setEduMediaConsent,
  type MediaConsentLevel,
} from '../../services/eduMediaConsentService'
import { mapEduServerError } from '../Education/classesBuilderHelpers'
import { useIsFamilyOnly } from '../../hooks/useFamilyAccess'
import { useWorkspace } from '../../context/useWorkspace'
import { requestWorkspaceSwitch } from '../../services/workspaceSwitchService'
import { useNls } from '../../nls/nlsProvider'
import { useAuth } from '../../context/useAuth'
import { ToastContext } from '../../context/toastContextCore'
import { getFamilyEduEvent } from '../../services/eduEventService'
import { startMemberOutboxWorker } from '../../services/memberOutboxService'
import { listFamilyEduPtmDays } from '../../services/eduPtmService'
import {
  subscribeToFamilyInbox,
  markFamilyInboxRead,
  setFamilyAlertCategory,
  type EduAlertCategory,
  type FamilyInboxRow,
} from '../../services/familyNotificationService'
import {
  registerMobilePushToken,
  getMobilePushPermissionState,
  type PushPermissionState,
} from '../../services/mobilePushRegistrationService'
import { loadUserNotificationPreferences } from '../../services/notificationService'
import { familyInboxRoute, type FamilyTabKey } from '../../utils/familyInboxRoute'
import { formatInboxTime } from '../../utils/eduFamilyFormat'
import './familyShell.css'

export type { FamilyTabKey }

export interface FamilyShellProps {
  learners: FamilyLearnerRow[]
}

const computeDefaultChildKey = (learners: FamilyLearnerRow[]): string => {
  if (learners.length === 0) return ''
  const nonFrozen = learners.find((l) => !l.frozen)
  const chosen = nonFrozen ?? learners[0]
  return `${chosen.workspaceId}_${chosen.learnerId}`
}

const getUrlChildKey = (): string => {
  if (typeof window === 'undefined') return ''
  const params = new URLSearchParams(window.location.search)
  return params.get('child') ?? ''
}

const VALID_TABS: Record<string, FamilyTabKey> = {
  today: 'today',
  book: 'book',
  work: 'work',
  progress: 'progress',
  class: 'class',
  more: 'more',
}

const getUrlDeepLink = (): {
  subTab?: 'announcements' | 'feed' | 'messages' | 'events' | 'meetings'
  announcementId?: string | null
  feedPostId?: string | null
  threadId?: string | null
  eventId?: string | null
  ptmDayId?: string | null
} => {
  if (typeof window === 'undefined') return {}
  const params = new URLSearchParams(window.location.search)
  const eventId = params.get('event')
  const ptmDayId = params.get('ptmDay')
  const announcementId = params.get('announcement')
  const feedPostId = params.get('feedPost')
  const threadId = params.get('thread')
  if (eventId) {
    const hasChild = params.has('child')
    return hasChild ? { subTab: 'events', eventId } : { subTab: 'events' }
  }
  if (ptmDayId) {
    const hasChild = params.has('child')
    return hasChild ? { subTab: 'meetings', ptmDayId } : { subTab: 'meetings' }
  }
  if (announcementId) {
    return { subTab: 'announcements', announcementId }
  }
  if (feedPostId) {
    return { subTab: 'feed', feedPostId }
  }
  if (threadId) {
    return { subTab: 'messages', threadId }
  }
  return {}
}

const getUrlTab = (): FamilyTabKey => {
  if (typeof window === 'undefined') return 'today'
  const params = new URLSearchParams(window.location.search)
  const tab = params.get('tab')
  if (tab && tab in VALID_TABS) {
    return VALID_TABS[tab]
  }
  if (
    params.has('event') ||
    params.has('ptmDay') ||
    params.has('announcement') ||
    params.has('feedPost') ||
    params.has('thread')
  ) {
    return 'class'
  }
  return 'today'
}

export const FamilyShell = ({ learners }: FamilyShellProps) => {
  const { t, locale } = useNls()
  const isFamilyOnly = useIsFamilyOnly()
  const { workspaces, getWorkspaceRole, rolesByWorkspaceId } = useWorkspace()

  const nonPreviewWorkspaces = useMemo(
    () => (workspaces || []).filter((w) => !w.isPreview),
    [workspaces],
  )
  const firstStaffWorkspace = useMemo(
    () =>
      nonPreviewWorkspaces.find((w) => {
        const role = getWorkspaceRole ? getWorkspaceRole(w.id) : rolesByWorkspaceId?.get(w.id)
        return role && role !== 'LEARNER'
      }),
    [nonPreviewWorkspaces, getWorkspaceRole, rolesByWorkspaceId],
  )

  const defaultChildKey = useMemo(() => computeDefaultChildKey(learners), [learners])

  useEffect(() => startMemberOutboxWorker(), [])

  const resolveChildKey = useCallback(
    (requested: string): string => {
      if (requested && learners.some((l) => `${l.workspaceId}_${l.learnerId}` === requested)) {
        return requested
      }
      return defaultChildKey
    },
    [learners, defaultChildKey],
  )

  const [selectedKey, setSelectedKey] = useState<string>(() => resolveChildKey(getUrlChildKey()))
  const [activeTab, setActiveTab] = useState<FamilyTabKey>(() => getUrlTab())
  const [classDeepLink, setClassDeepLink] = useState<{
    subTab?: 'announcements' | 'feed' | 'messages' | 'events' | 'meetings'
    announcementId?: string | null
    feedPostId?: string | null
    threadId?: string | null
    eventId?: string | null
    ptmDayId?: string | null
  }>(() => getUrlDeepLink())
  const initialHadChildRef = useRef<boolean>(
    typeof window !== 'undefined' ? new URLSearchParams(window.location.search).has('child') : true,
  )
  const initialEventIdRef = useRef<string | null>(
    typeof window !== 'undefined' ? new URLSearchParams(window.location.search).get('event') : null,
  )
  const initialPtmDayIdRef = useRef<string | null>(
    typeof window !== 'undefined' ? new URLSearchParams(window.location.search).get('ptmDay') : null,
  )
  const toastContext = useContext(ToastContext)
  const [classCounts, setClassCounts] = useState<{
    unreadAnnouncements: number
    newFeedPosts: number
    unreadMessages: number
  }>({
    unreadAnnouncements: 0,
    newFeedPosts: 0,
    unreadMessages: 0,
  })

  const updateUrlChildKey = useCallback((key: string) => {
    if (typeof window === 'undefined') return
    try {
      const href = window.location.href || 'http://localhost/family'
      const url = new URL(href, window.location.origin || 'http://localhost')
      let changed = false
      if (url.searchParams.get('child') !== key) {
        url.searchParams.set('child', key)
        changed = true
      }
      if (url.searchParams.has('tab')) {
        url.searchParams.delete('tab')
        changed = true
      }
      if (changed && window.history?.replaceState) {
        window.history.replaceState(window.history.state, '', url.pathname + url.search)
      }
    } catch {
      // Safe fallback if jsdom mock location is partial
    }
  }, [])

  // Keep URL search query in sync with selected child
  useEffect(() => {
    updateUrlChildKey(selectedKey)
  }, [selectedKey, updateUrlChildKey])

  // Deep link without child resolution for events (ED16.b2)
  useEffect(() => {
    const eventId = initialEventIdRef.current
    const hadChildParam = initialHadChildRef.current

    if (eventId && !hadChildParam && learners.length > 0) {
      let active = true
      const resolveEventChild = async () => {
        const currentChild =
          learners.find((l) => `${l.workspaceId}_${l.learnerId}` === selectedKey) || learners[0]

        // 1. Try selected child
        try {
          const res = await getFamilyEduEvent({
            workspaceId: currentChild.workspaceId,
            learnerId: currentChild.learnerId,
            eventId,
          })
          if (!active) return
          if (res?.event) {
            initialHadChildRef.current = true
            setClassDeepLink({ subTab: 'events', eventId })
            return
          }
        } catch {
          // Selected child not allowed; try other children
        }

        // 2. Try family's other children in order
        for (const child of learners) {
          if (
            `${child.workspaceId}_${child.learnerId}` ===
            `${currentChild.workspaceId}_${currentChild.learnerId}`
          ) {
            continue
          }
          try {
            const res = await getFamilyEduEvent({
              workspaceId: child.workspaceId,
              learnerId: child.learnerId,
              eventId,
            })
            if (!active) return
            if (res?.event) {
              const newKey = `${child.workspaceId}_${child.learnerId}`
              initialHadChildRef.current = true
              setSelectedKey(newKey)
              updateUrlChildKey(newKey)
              setClassDeepLink({ subTab: 'events', eventId })
              return
            }
          } catch {
            // Not allowed
          }
        }

        if (!active) return
        // 3. None allowed: show toast and no dialog
        initialHadChildRef.current = true
        setClassDeepLink({ subTab: 'events' })
        const errorMsg = t('NLSFamilyEventNotFound')
        toastContext?.showToast(errorMsg, 'error')
      }

      void resolveEventChild()
      return () => {
        active = false
      }
    }
  }, [learners, selectedKey, updateUrlChildKey, t, toastContext])

  // Deep link without child resolution for PTM days (ED16.b4)
  useEffect(() => {
    const ptmDayId = initialPtmDayIdRef.current
    const hadChildParam = initialHadChildRef.current

    if (ptmDayId && !hadChildParam && learners.length > 0) {
      let active = true
      const resolvePtmChild = async () => {
        const currentChild =
          learners.find((l) => `${l.workspaceId}_${l.learnerId}` === selectedKey) || learners[0]

        // 1. Try selected child
        try {
          const res = await listFamilyEduPtmDays({
            workspaceId: currentChild.workspaceId,
            learnerId: currentChild.learnerId,
          })
          if (!active) return
          if (res?.days?.some((d) => d.id === ptmDayId)) {
            initialHadChildRef.current = true
            setClassDeepLink({ subTab: 'meetings', ptmDayId })
            return
          }
        } catch {
          // Selected child not allowed; try other children
        }

        // 2. Try family's other children in order
        for (const child of learners) {
          if (
            `${child.workspaceId}_${child.learnerId}` ===
            `${currentChild.workspaceId}_${currentChild.learnerId}`
          ) {
            continue
          }
          try {
            const res = await listFamilyEduPtmDays({
              workspaceId: child.workspaceId,
              learnerId: child.learnerId,
            })
            if (!active) return
            if (res?.days?.some((d) => d.id === ptmDayId)) {
              const newKey = `${child.workspaceId}_${child.learnerId}`
              initialHadChildRef.current = true
              setSelectedKey(newKey)
              updateUrlChildKey(newKey)
              setClassDeepLink({ subTab: 'meetings', ptmDayId })
              return
            }
          } catch {
            // Not allowed
          }
        }

        if (!active) return
        // 3. None allowed: show toast and no dialog
        initialHadChildRef.current = true
        setClassDeepLink({ subTab: 'meetings' })
        const errorMsg = t('NLSFamilyPtmDayNotFound')
        toastContext?.showToast(errorMsg, 'error')
      }

      void resolvePtmChild()
      return () => {
        active = false
      }
    }
  }, [learners, selectedKey, updateUrlChildKey, t, toastContext])

  // Listen for browser back / forward navigation
  useEffect(() => {
    const onPopState = () => {
      const fromUrl = getUrlChildKey()
      setSelectedKey(resolveChildKey(fromUrl))
      const tabFromUrl = getUrlTab()
      setActiveTab(tabFromUrl)
      setClassDeepLink(getUrlDeepLink())
      if (typeof window !== 'undefined') {
        const url = new URL(window.location.href)
        if (url.searchParams.has('tab')) {
          url.searchParams.delete('tab')
          window.history.replaceState(window.history.state, '', url.pathname + url.search)
        }
      }
    }

    window.addEventListener('popstate', onPopState)
    return () => {
      window.removeEventListener('popstate', onPopState)
    }
  }, [resolveChildKey])

  const handleSelectChild = (key: string) => {
    setSelectedKey(key)
    updateUrlChildKey(key)
  }

  const handleOpenStaffWorkspace = () => {
    const fromWsId = (window.history.state as { fromWorkspaceId?: string } | null)?.fromWorkspaceId
    const targetWsId =
      fromWsId && nonPreviewWorkspaces.some((w) => w.id === fromWsId)
        ? fromWsId
        : firstStaffWorkspace?.id
    if (targetWsId) {
      void requestWorkspaceSwitch({
        toWorkspaceId: targetWsId,
        source: 'USER',
        landing: '/',
      })
    }
  }

  const { currentUser } = useAuth()
  const uid = currentUser?.uid ?? null
  const [inboxRows, setInboxRows] = useState<FamilyInboxRow[]>([])
  const [isAlertsOpen, setIsAlertsOpen] = useState(false)

  // Push permission and banner state
  const [pushPermission, setPushPermission] = useState<PushPermissionState>('granted')
  const [showBanner, setShowBanner] = useState<boolean>(false)

  const isBannerDismissed = useCallback((userUid: string): boolean => {
    try {
      return window.localStorage.getItem(`timelining.familyAlertsBannerDismissed.${userUid}`) === 'true'
    } catch {
      return false
    }
  }, [])

  useEffect(() => {
    let cancelled = false
    void getMobilePushPermissionState().then((perm) => {
      if (cancelled) return
      setPushPermission(perm)
      if (perm === 'prompt' && uid && !isBannerDismissed(uid)) {
        setShowBanner(true)
      } else {
        setShowBanner(false)
      }
    })
    return () => {
      cancelled = true
    }
  }, [uid, isBannerDismissed])

  const handleTurnOnAlerts = async () => {
    if (!uid) return
    setShowBanner(false)
    try {
      await registerMobilePushToken(uid, 'FAMILY', {
        force: true,
        onPermissionChange: (state) => {
          setPushPermission(state)
        },
      })
    } catch (err) {
      console.warn('Failed to register mobile push token:', err)
    }
  }

  const handleDismissBanner = () => {
    if (uid) {
      try {
        window.localStorage.setItem(`timelining.familyAlertsBannerDismissed.${uid}`, 'true')
      } catch {
        // ignore
      }
    }
    setShowBanner(false)
  }

  // Alert category preferences state
  const [isSettingsOpen, setIsSettingsOpen] = useState(false)
  const [settingsError, setSettingsError] = useState<string | null>(null)
  const [categoryToggles, setCategoryToggles] = useState<Record<string, boolean>>({
    EDU_ATTENDANCE: true,
    EDU_LEAVE: true,
    EDU_HOMEWORK: true,
    EDU_CLASSES: true,
    EDU_TODAY: true,
    EDU_REPORTS: true,
    EDU_FEES: true,
    EDU_BOOKINGS: true,
    EDU_PROGRESS: true,
    EDU_ANNOUNCEMENTS: true,
    EDU_MESSAGES: true,
    EDU_EVENTS: true,
  })

  useEffect(() => {
    if (!uid) return
    let cancelled = false
    void loadUserNotificationPreferences(uid)
      .then((prefs) => {
        if (cancelled) return
        const cats = prefs.categories ?? {}
        setCategoryToggles({
          EDU_ATTENDANCE: cats['EDU_ATTENDANCE']?.inApp ?? true,
          EDU_LEAVE: cats['EDU_LEAVE']?.inApp ?? true,
          EDU_HOMEWORK: cats['EDU_HOMEWORK']?.inApp ?? true,
          EDU_CLASSES: cats['EDU_CLASSES']?.inApp ?? true,
          EDU_TODAY: cats['EDU_TODAY']?.inApp ?? true,
          EDU_REPORTS: cats['EDU_REPORTS']?.inApp ?? true,
          EDU_FEES: cats['EDU_FEES']?.inApp ?? true,
          EDU_BOOKINGS: cats['EDU_BOOKINGS']?.inApp ?? true,
          EDU_PROGRESS: cats['EDU_PROGRESS']?.inApp ?? true,
          EDU_ANNOUNCEMENTS: cats['EDU_ANNOUNCEMENTS']?.inApp ?? true,
          EDU_MESSAGES: cats['EDU_MESSAGES']?.inApp ?? true,
          EDU_EVENTS: cats['EDU_EVENTS']?.inApp ?? true,
        })
      })
      .catch((err) => {
        console.warn('Failed to load user notification preferences:', err)
      })
    return () => {
      cancelled = true
    }
  }, [uid, isSettingsOpen])

  const handleToggleCategory = async (category: EduAlertCategory, nextValue: boolean) => {
    if (!uid) return
    setSettingsError(null)
    setCategoryToggles((prev) => ({ ...prev, [category]: nextValue }))
    try {
      await setFamilyAlertCategory(uid, category, nextValue)
    } catch (err) {
      console.warn('Failed to set family alert category:', err)
      setCategoryToggles((prev) => ({ ...prev, [category]: !nextValue }))
      setSettingsError(t('NLSFamilyAlertsSaveError'))
    }
  }

  useEffect(() => {
    if (!uid) {
      return
    }
    const unsubscribe = subscribeToFamilyInbox(uid, (rows) => {
      setInboxRows(rows)
    })
    return () => {
      unsubscribe()
    }
  }, [uid])

  const displayedInboxRows = useMemo(() => (uid ? inboxRows : []), [uid, inboxRows])

  const unreadCount = useMemo(() => {
    return displayedInboxRows.filter((r) => r.unread).length
  }, [displayedInboxRows])

  const childRows: ChildSwitcherItem[] = useMemo(() => {
    return learners.map((l) => {
      const parts = [l.workspaceName, l.classNames.join(', ')].filter(Boolean)
      return {
        key: `${l.workspaceId}_${l.learnerId}`,
        name: l.displayName,
        photoUrl: l.photoUrl,
        secondaryLine: parts.join(' · '),
        frozen: l.frozen,
      }
    })
  }, [learners])

  const childMap = useMemo(() => {
    const map = new Map<string, string>()
    for (const row of childRows) {
      map.set(row.key, row.name)
    }
    return map
  }, [childRows])

  const handleMarkAllRead = useCallback(async () => {
    const unreadIds = displayedInboxRows.filter((r) => r.unread).map((r) => r.notificationId)
    if (unreadIds.length > 0) {
      try {
        await markFamilyInboxRead(Array.from(new Set(unreadIds)))
      } catch (err) {
        console.warn('Failed to mark all notifications read:', err)
      }
    }
  }, [displayedInboxRows])

  const handleRowClick = (row: FamilyInboxRow) => {
    markFamilyInboxRead([row.notificationId]).catch((err) => {
      console.warn('Failed to mark notification read:', err)
    })
    if (row.childKey && childMap.has(row.childKey)) {
      handleSelectChild(row.childKey)
      const targetChild = learners.find(
        (l) => `${l.workspaceId}_${l.learnerId}` === row.childKey,
      )
      const targetHasTraining = Boolean(
        targetChild &&
        Array.isArray(targetChild.trainingProgrammes) &&
        targetChild.trainingProgrammes.length > 0,
      )
      setActiveTab(familyInboxRoute(row.category, { hasTraining: targetHasTraining }) ?? 'today')
    }
    setIsAlertsOpen(false)
  }

  const dialogActions: AppDialogAction[] = useMemo(
    () => [
      {
        label: t('NLSFamilyAlertsSettings'),
        variant: 'secondary',
        onClick: () => {
          setIsAlertsOpen(false)
          setIsSettingsOpen(true)
        },
      },
      {
        label: t('NLSFamilyAlertsMarkAllRead'),
        variant: 'secondary',
        onClick: handleMarkAllRead,
        disabled: unreadCount === 0,
      },
      {
        label: t('NLSFamilyAlertsClose'),
        variant: 'primary',
        onClick: () => setIsAlertsOpen(false),
      },
    ],
    [t, unreadCount, handleMarkAllRead],
  )

  const selectedChild = useMemo(() => {
    return learners.find((l) => `${l.workspaceId}_${l.learnerId}` === selectedKey) ?? learners[0]
  }, [learners, selectedKey])

  const currentChildKey = selectedChild
    ? `${selectedChild.workspaceId}_${selectedChild.learnerId}`
    : null
  const [loadedConsentChildKey, setLoadedConsentChildKey] = useState<string | null>(null)
  const isConsentLoading = Boolean(
    isSettingsOpen && currentChildKey && loadedConsentChildKey !== currentChildKey,
  )

  const [mediaConsentLevel, setMediaConsentLevel] = useState<MediaConsentLevel>('NONE')
  const [mediaConsentSetBy, setMediaConsentSetBy] = useState<string | null>(null)
  const [mediaConsentSetByName, setMediaConsentSetByName] = useState<string | null>(null)
  const [mediaConsentSetAt, setMediaConsentSetAt] = useState<string | null>(null)
  const [savingMediaConsent, setSavingMediaConsent] = useState<boolean>(false)
  const [mediaConsentError, setMediaConsentError] = useState<string | null>(null)

  const effectiveMediaConsentLevel = isConsentLoading ? 'NONE' : mediaConsentLevel
  const isConsentDisabled = isConsentLoading || savingMediaConsent

  useEffect(() => {
    if (!isSettingsOpen || !selectedChild || !currentChildKey) return
    let active = true

    getEduMediaConsent({
      workspaceId: selectedChild.workspaceId,
      learnerId: selectedChild.learnerId,
    })
      .then((res) => {
        if (!active) return
        setLoadedConsentChildKey(currentChildKey)
        if (res.consent) {
          setMediaConsentLevel(res.consent.level || 'NONE')
          setMediaConsentSetBy(res.consent.setBy)
          setMediaConsentSetByName(res.consent.setByName ?? null)
          setMediaConsentSetAt(res.consent.setAt)
        }
      })
      .catch((err) => {
        if (!active) return
        setLoadedConsentChildKey(currentChildKey)
        setMediaConsentError(mapEduServerError(err, t))
      })

    return () => {
      active = false
    }
  }, [isSettingsOpen, selectedChild, currentChildKey, t])

  const isSelectedChildMinorSelf = Boolean(
    selectedChild?.isMinor && selectedChild?.relation === 'SELF',
  )

  const handleSetMediaConsent = async (nextLevel: MediaConsentLevel) => {
    if (!selectedChild || isSelectedChildMinorSelf || isConsentDisabled) return
    const prevLevel = mediaConsentLevel
    const prevSetBy = mediaConsentSetBy
    const prevSetByName = mediaConsentSetByName
    const prevSetAt = mediaConsentSetAt
    setMediaConsentLevel(nextLevel)
    setSavingMediaConsent(true)
    setMediaConsentError(null)

    try {
      const res = await setEduMediaConsent({
        workspaceId: selectedChild.workspaceId,
        learnerId: selectedChild.learnerId,
        level: nextLevel,
      })
      if (res.consent) {
        setMediaConsentLevel(res.consent.level)
        setMediaConsentSetBy(res.consent.setBy)
        setMediaConsentSetByName(res.consent.setByName ?? null)
        setMediaConsentSetAt(res.consent.setAt)
      }
    } catch (err) {
      setMediaConsentLevel(prevLevel)
      setMediaConsentSetBy(prevSetBy)
      setMediaConsentSetByName(prevSetByName)
      setMediaConsentSetAt(prevSetAt)
      setMediaConsentError(mapEduServerError(err, t))
    } finally {
      setSavingMediaConsent(false)
    }
  }

  const mediaConsentSetInfo = useMemo(() => {
    if (!mediaConsentSetAt) return null
    let dateStr: string
    try {
      const d = new Date(mediaConsentSetAt)
      dateStr = d.toLocaleDateString()
    } catch {
      dateStr = String(mediaConsentSetAt)
    }
    if (mediaConsentSetBy && currentUser?.uid && mediaConsentSetBy === currentUser.uid) {
      return t('NLSFamilyMediaConsentSetBy', { setBy: t('NLSFamilyMediaConsentYou'), date: dateStr })
    }
    if (mediaConsentSetByName) {
      return t('NLSFamilyMediaConsentSetBy', { setBy: mediaConsentSetByName, date: dateStr })
    }
    return t('NLSFamilyMediaConsentSetOn', { date: dateStr })
  }, [mediaConsentSetAt, mediaConsentSetBy, mediaConsentSetByName, currentUser, t])

  const currentConsentLabel = useMemo(() => {
    switch (mediaConsentLevel) {
      case 'PHOTOS_OK':
        return t('NLSFamilyMediaConsentPhotosOk')
      case 'CLASS_ONLY':
        return t('NLSFamilyMediaConsentClassOnly')
      case 'NONE':
      default:
        return t('NLSFamilyMediaConsentNone')
    }
  }, [mediaConsentLevel, t])


  useEffect(() => {
    if (!selectedChild || selectedChild.frozen) return
    let active = true

    Promise.all([
      listFamilyAnnouncements({
        workspaceId: selectedChild.workspaceId,
        learnerId: selectedChild.learnerId,
      }).catch(() => ({ announcements: [] })),
      listFamilyFeed({
        workspaceId: selectedChild.workspaceId,
        learnerId: selectedChild.learnerId,
      }).catch(() => ({ posts: [] })),
      listEduThreads({
        workspaceId: selectedChild.workspaceId,
        learnerId: selectedChild.learnerId,
      }).catch(() => ({ threads: [] })),
    ]).then(([annRes, feedRes, thrRes]) => {
      if (!active) return
      const unreadAnnouncements = (annRes.announcements || []).filter((a) => !a.readByMe).length
      const newFeedPosts = (feedRes.posts || []).filter((p) => !p.seenByMe).length
      const unreadMessages = (thrRes.threads || []).reduce(
        (sum, t) => sum + (t.unreadCountFamily || 0),
        0,
      )
      setClassCounts({
        unreadAnnouncements,
        newFeedPosts,
        unreadMessages,
      })
    })

    return () => {
      active = false
    }
  }, [selectedChild])

  const hasTraining = Boolean(
    selectedChild &&
    Array.isArray(selectedChild.trainingProgrammes) &&
    selectedChild.trainingProgrammes.length > 0,
  )

  const currentTab = activeTab === 'book' && !hasTraining ? 'today' : activeTab

  const tourGuide = useContext(TourGuideContext)
  const isTourActive = Boolean(tourGuide?.isTourActive)
  const isLauncherHidden = isAlertsOpen || isSettingsOpen
  const activeScreenGuideId = isLauncherHidden ? undefined : familyScreenGuideId(currentTab)

  const classBadgeCount = classCounts.unreadAnnouncements + classCounts.unreadMessages

  const tabs: FamilyTabSpec<FamilyTabKey>[] = useMemo(() => {
    const list: FamilyTabSpec<FamilyTabKey>[] = [
      {
        key: 'today',
        label: t('NLSFamilyTabToday'),
        icon: <TodayRoundedIcon sx={{ fontSize: 20 }} />,
      },
    ]

    if (hasTraining) {
      list.push({
        key: 'book',
        label: t('NLSFamilyTabBook'),
        icon: <EventAvailableRoundedIcon sx={{ fontSize: 20 }} />,
      })
    }

    list.push(
      {
        key: 'work',
        label: t('NLSFamilyTabWork'),
        icon: <AssignmentRoundedIcon sx={{ fontSize: 20 }} />,
      },
      {
        key: 'progress',
        label: t('NLSFamilyTabProgress'),
        icon: <TrendingUpRoundedIcon sx={{ fontSize: 20 }} />,
      },
      {
        key: 'class',
        label: t('NLSFamilyTabClass'),
        icon: <SchoolRoundedIcon sx={{ fontSize: 20 }} />,
        unread: classBadgeCount > 0,
        count: classBadgeCount > 0 ? classBadgeCount : undefined,
      },
    )

    return list
  }, [t, hasTraining, classBadgeCount])

  return (
    <main className="familyShell" aria-label={t('NLSFamilyAriaLearners')}>
      <div className="familyShell__inner">
        <header className="familyShell__header">
          <ChildSwitcher
            items={childRows}
            selectedKey={selectedKey}
            onSelect={handleSelectChild}
            ariaLabel={t('NLSFamilyChildSwitcherAria')}
            sheetTitle={t('NLSFamilySwitchChildTitle')}
          />
          <div className="familyShell__headerActions">
            <ScreenGuideHost
              screenId={activeScreenGuideId}
              disabled={isTourActive}
              className="screenGuideLauncher--topbar"
            />
            <IconButton
              label={t('NLSFamilyHeaderMenuAria')}
              aria-label={t('NLSFamilyHeaderMenuAria')}
              onClick={() => setActiveTab('more')}
              icon={<MoreHorizRoundedIcon sx={{ fontSize: 20 }} />}
            />
            <IconButton
              label={t('NLSFamilyAlertsButtonAria', { count: unreadCount })}
              aria-label={t('NLSFamilyAlertsButtonAria', { count: unreadCount })}
              onClick={() => setIsAlertsOpen(true)}
              icon={
                <span className="familyShell__alertIconWrap">
                  <NotificationsNoneRoundedIcon sx={{ fontSize: 20 }} />
                  {unreadCount > 0 && (
                    <span className="familyShell__alertBadge" aria-hidden="true">
                      {unreadCount > 99 ? '99+' : unreadCount}
                    </span>
                  )}
                </span>
              }
            />
            {!isFamilyOnly && firstStaffWorkspace && (
              <Button variant="secondary" size="sm" onClick={handleOpenStaffWorkspace}>
                {t('NLSFamilyStaffView')}
              </Button>
            )}
          </div>
        </header>

        {showBanner && (
          <FamilyCard className="familyShell__alertsBanner">
            <div className="familyShell__bannerContent">
              <p className="familyShell__bannerText">{t('NLSFamilyAlertsBannerText')}</p>
              <div className="familyShell__bannerActions">
                <Button variant="primary" size="sm" onClick={handleTurnOnAlerts}>
                  {t('NLSFamilyAlertsTurnOn')}
                </Button>
                <Button variant="secondary" size="sm" onClick={handleDismissBanner}>
                  {t('NLSFamilyAlertsNotNow')}
                </Button>
              </div>
            </div>
          </FamilyCard>
        )}

        <AppDialog
          open={isAlertsOpen}
          title={t('NLSFamilyAlertsDialogTitle')}
          size="md"
          onDismiss={() => setIsAlertsOpen(false)}
          actions={dialogActions}
        >
          {displayedInboxRows.length === 0 ? (
            <div className="familyShell__inboxEmpty">
              {t('NLSFamilyAlertsEmpty')}
            </div>
          ) : (
            <div className="familyShell__inboxList">
              {displayedInboxRows.map((row) => {
                const childName = row.childKey ? childMap.get(row.childKey) : null
                const timeStr = formatInboxTime(row.createdAtMs, locale)
                const meta = childName ? `${childName} · ${timeStr}` : timeStr
                return (
                  <InboxItemRow
                    key={row.rowId}
                    title={row.title}
                    body={row.body}
                    meta={meta}
                    unread={row.unread}
                    unreadLabel={t('NLSFamilyAlertsUnreadItem')}
                    onClick={() => handleRowClick(row)}
                  />
                )
              })}
            </div>
          )}
        </AppDialog>

        <AppDialog
          open={isSettingsOpen}
          title={t('NLSFamilyAlertsSettings')}
          size="md"
          onDismiss={() => {
            setIsSettingsOpen(false)
            setLoadedConsentChildKey(null)
          }}
          actions={[
            {
              label: t('NLSFamilyAlertsClose'),
              variant: 'primary',
              onClick: () => {
                setIsSettingsOpen(false)
                setLoadedConsentChildKey(null)
              },
            },
          ]}
        >
          <div className="familyShell__settingsList">
            {settingsError && (
              <div className="familyShell__settingsError" role="alert">
                {settingsError}
              </div>
            )}
            <div className="familyShell__settingsItem">
              <Toggle
                checked={categoryToggles['EDU_ATTENDANCE']}
                onChange={(next) => handleToggleCategory('EDU_ATTENDANCE', next)}
                label={t('NLSFamilyAlertsCategoryAttendance')}
                onLabel={t('NLSFamilyAlertsOn')}
                offLabel={t('NLSFamilyAlertsOff')}
              />
            </div>
            <div className="familyShell__settingsItem">
              <Toggle
                checked={categoryToggles['EDU_LEAVE']}
                onChange={(next) => handleToggleCategory('EDU_LEAVE', next)}
                label={t('NLSFamilyAlertsCategoryLeave')}
                onLabel={t('NLSFamilyAlertsOn')}
                offLabel={t('NLSFamilyAlertsOff')}
              />
            </div>
            <div className="familyShell__settingsItem">
              <Toggle
                checked={categoryToggles['EDU_HOMEWORK']}
                onChange={(next) => handleToggleCategory('EDU_HOMEWORK', next)}
                label={t('NLSFamilyAlertsCategoryHomework')}
                onLabel={t('NLSFamilyAlertsOn')}
                offLabel={t('NLSFamilyAlertsOff')}
              />
            </div>
            <div className="familyShell__settingsItem">
              <Toggle
                checked={categoryToggles['EDU_CLASSES']}
                onChange={(next) => handleToggleCategory('EDU_CLASSES', next)}
                label={t('NLSFamilyAlertsCategoryClasses')}
                onLabel={t('NLSFamilyAlertsOn')}
                offLabel={t('NLSFamilyAlertsOff')}
              />
            </div>
            <div className="familyShell__settingsItem">
              <Toggle
                checked={categoryToggles['EDU_TODAY']}
                onChange={(next) => handleToggleCategory('EDU_TODAY', next)}
                label={t('NLSFamilyAlertsCategoryToday')}
                onLabel={t('NLSFamilyAlertsOn')}
                offLabel={t('NLSFamilyAlertsOff')}
              />
            </div>
            <div className="familyShell__settingsItem">
              <Toggle
                checked={categoryToggles['EDU_REPORTS']}
                onChange={(next) => handleToggleCategory('EDU_REPORTS', next)}
                label={t('NLSFamilyAlertEduReports')}
                onLabel={t('NLSFamilyAlertsOn')}
                offLabel={t('NLSFamilyAlertsOff')}
              />
            </div>
            <div className="familyShell__settingsItem">
              <Toggle
                checked={categoryToggles['EDU_FEES']}
                onChange={(next) => handleToggleCategory('EDU_FEES', next)}
                label={t('NLSFamilyAlertEduFees')}
                onLabel={t('NLSFamilyAlertsOn')}
                offLabel={t('NLSFamilyAlertsOff')}
              />
            </div>
            <div className="familyShell__settingsItem">
              <Toggle
                checked={categoryToggles['EDU_BOOKINGS']}
                onChange={(next) => handleToggleCategory('EDU_BOOKINGS', next)}
                label={t('NLSFamilyAlertEduBookings')}
                onLabel={t('NLSFamilyAlertsOn')}
                offLabel={t('NLSFamilyAlertsOff')}
              />
            </div>
            <div className="familyShell__settingsItem">
              <Toggle
                checked={categoryToggles['EDU_PROGRESS']}
                onChange={(next) => handleToggleCategory('EDU_PROGRESS', next)}
                label={t('NLSFamilyAlertEduProgress')}
                onLabel={t('NLSFamilyAlertsOn')}
                offLabel={t('NLSFamilyAlertsOff')}
              />
            </div>
            <div className="familyShell__settingsItem">
              <Toggle
                checked={categoryToggles['EDU_ANNOUNCEMENTS']}
                onChange={(next) => handleToggleCategory('EDU_ANNOUNCEMENTS', next)}
                label={t('NLSFamilyAlertEduAnnouncements')}
                onLabel={t('NLSFamilyAlertsOn')}
                offLabel={t('NLSFamilyAlertsOff')}
              />
            </div>
            <div className="familyShell__settingsItem">
              <Toggle
                checked={categoryToggles['EDU_MESSAGES']}
                onChange={(next) => handleToggleCategory('EDU_MESSAGES', next)}
                label={t('NLSFamilyAlertEduMessages')}
                onLabel={t('NLSFamilyAlertsOn')}
                offLabel={t('NLSFamilyAlertsOff')}
              />
            </div>
            <div className="familyShell__settingsItem">
              <Toggle
                checked={categoryToggles['EDU_EVENTS']}
                onChange={(next) => handleToggleCategory('EDU_EVENTS', next)}
                label={t('NLSFamilyAlertEduEvents')}
                onLabel={t('NLSFamilyAlertsOn')}
                offLabel={t('NLSFamilyAlertsOff')}
              />
            </div>

            <div className="familyShell__pushRow">
              {(() => {
                if (pushPermission === 'granted') {
                  return (
                    <span className="familyShell__pushStatus">
                      {t('NLSFamilyAlertsPushOn')}
                    </span>
                  )
                }
                if (pushPermission === 'prompt') {
                  return (
                    <Button variant="secondary" size="sm" onClick={handleTurnOnAlerts}>
                      {t('NLSFamilyAlertsTurnOn')}
                    </Button>
                  )
                }
                const hasNotification = typeof window !== 'undefined' && 'Notification' in window
                if (hasNotification && window.Notification?.permission === 'denied') {
                  return (
                    <span className="familyShell__pushStatus">
                      {t('NLSFamilyAlertsPushBlocked')}
                    </span>
                  )
                }
                return (
                  <span className="familyShell__pushStatus">
                    {t('NLSFamilyAlertsPushUnsupported')}
                  </span>
                )
              })()}
            </div>

            {selectedChild && (
              <div className="familyShell__mediaConsentSection" data-testid="media-consent-section">
                <div className="familyShell__settingsDivider" />
                <h4 className="familyShell__settingsSectionTitle">
                  {t('NLSFamilyMediaConsentTitle')}
                </h4>
                <p className="familyShell__settingsSectionDesc">
                  {t('NLSFamilyMediaConsentExplanation')}
                </p>
                {mediaConsentError && (
                  <div
                    className="familyShell__settingsError"
                    role="alert"
                    data-testid="media-consent-error"
                  >
                    {mediaConsentError}
                  </div>
                )}
                {isConsentLoading && (
                  <div className="familyShell__mediaConsentLoading" data-testid="media-consent-loading">
                    <MiniLoader />
                  </div>
                )}
                {isSelectedChildMinorSelf ? (
                  <div className="familyShell__mediaConsentReadOnly" data-testid="media-consent-readonly">
                    <span className="familyShell__mediaConsentCurrentBadge">
                      {currentConsentLabel}
                    </span>
                    <span className="familyShell__mediaConsentMinorNote">
                      {t('NLSFamilyMediaConsentMinorReadOnly')}
                    </span>
                  </div>
                ) : (
                  <div
                    className={`familyShell__mediaConsentControl ${isConsentDisabled ? 'is-disabled' : ''}`}
                    data-testid="media-consent-control"
                  >
                    <SegmentedControl<MediaConsentLevel>
                      ariaLabel={t('NLSFamilyMediaConsentTitle')}
                      value={effectiveMediaConsentLevel}
                      disabled={isConsentDisabled}
                      onChange={(level) => void handleSetMediaConsent(level)}
                      options={[
                        { value: 'PHOTOS_OK', label: t('NLSFamilyMediaConsentPhotosOk') },
                        { value: 'CLASS_ONLY', label: t('NLSFamilyMediaConsentClassOnly') },
                        { value: 'NONE', label: t('NLSFamilyMediaConsentNone') },
                      ]}
                    />
                  </div>
                )}
                <div className="familyShell__mediaConsentDescriptions">
                  <div className="familyShell__mediaConsentDescItem">
                    <strong>{t('NLSFamilyMediaConsentPhotosOk')}:</strong>{' '}
                    {t('NLSFamilyMediaConsentPhotosOkDesc')}
                  </div>
                  <div className="familyShell__mediaConsentDescItem">
                    <strong>{t('NLSFamilyMediaConsentClassOnly')}:</strong>{' '}
                    {t('NLSFamilyMediaConsentClassOnlyDesc')}
                  </div>
                  <div className="familyShell__mediaConsentDescItem">
                    <strong>{t('NLSFamilyMediaConsentNone')}:</strong>{' '}
                    {t('NLSFamilyMediaConsentNoneDesc')}
                  </div>
                </div>
                {mediaConsentSetInfo && (
                  <div className="familyShell__mediaConsentSetInfo" data-testid="media-consent-set-info">
                    <span data-testid="media-consent-updated">{mediaConsentSetInfo}</span>
                  </div>
                )}
              </div>
            )}
          </div>
        </AppDialog>


        <FamilyTabs
          items={tabs}
          activeTab={currentTab}
          onChange={setActiveTab}
          ariaLabel={t('NLSFamilyTabsAria')}
        />

        <section className="familyShell__body">
          {selectedChild && currentTab === 'today' && (
            <FamilyTodayTab
              workspaceId={selectedChild.workspaceId}
              learnerId={selectedChild.learnerId}
              frozen={selectedChild.frozen}
              onOpenWorkTab={() => setActiveTab('work')}
              onOpenClassTab={(subTab) => {
                setActiveTab('class')
                setClassDeepLink({ subTab })
              }}
              classCounts={classCounts}
            />
          )}
          {selectedChild && currentTab === 'book' && hasTraining && (
            <FamilyBookTab
              workspaceId={selectedChild.workspaceId}
              learnerId={selectedChild.learnerId}
              trainingProgrammes={selectedChild.trainingProgrammes || []}
              frozen={selectedChild.frozen}
            />
          )}
          {selectedChild && currentTab === 'work' && (
            <FamilyWorkTab
              workspaceId={selectedChild.workspaceId}
              learnerId={selectedChild.learnerId}
              frozen={selectedChild.frozen}
            />
          )}
          {selectedChild && currentTab === 'progress' && (
            <FamilyProgressTab
              workspaceId={selectedChild.workspaceId}
              learnerId={selectedChild.learnerId}
              trainingProgrammes={selectedChild.trainingProgrammes || []}
              frozen={selectedChild.frozen}
              learner={selectedChild}
            />
          )}
          {selectedChild && currentTab === 'class' && (
            <FamilyClassTab
              workspaceId={selectedChild.workspaceId}
              learnerId={selectedChild.learnerId}
              learner={selectedChild}
              otherLearners={learners.filter(
                (l) => `${l.workspaceId}_${l.learnerId}` !== selectedKey,
              )}
              frozen={selectedChild.frozen}
              initialSubTab={classDeepLink.subTab}
              initialAnnouncementId={classDeepLink.announcementId}
              initialFeedPostId={classDeepLink.feedPostId}
              initialThreadId={classDeepLink.threadId}
              initialEventId={classDeepLink.eventId}
              initialPtmDayId={classDeepLink.ptmDayId}
              onCountsChange={setClassCounts}
            />
          )}
          {selectedChild && currentTab === 'more' && (
            <FamilyMoreTab
              workspaceId={selectedChild.workspaceId}
              learnerId={selectedChild.learnerId}
              frozen={selectedChild.frozen}
              onOpenMeetings={() => {
                setActiveTab('class')
                setClassDeepLink({ subTab: 'meetings' })
              }}
            />
          )}
        </section>
      </div>
    </main>

  )
}
